import React from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { GameButton } from '@/components/ui';
import { FONT, FONT_SIZE, SPACING, RADIUS, COLOR } from '@/config/designTokens';
import Character3D from '@/components/game/elements/Character3D';
import Enemy3D from '@/components/game/elements/Enemy3D';
import type { WorldEnemyType, EnemyState } from '@/types/enemy';
import type { Position } from '@/types/game';
import { ENEMY_DEFINITIONS, WORLD_ENEMY_MAP } from '@/config/enemies';
import { WORLDS } from '@/config/worlds';

const ENEMY_TYPES: WorldEnemyType[] = [
  'bramble_beetle',
  'tunnel_drone',
  'skid_mite',
  'cinder_hound',
  'echo_mirror',
];

/** Maps each enemy type to its world context label and theme color. */
const ENEMY_WORLD_CONTEXT: Record<WorldEnemyType, { worldName: string; levelRange: string; themeColor: string }> = (() => {
  const ctx = {} as Record<WorldEnemyType, { worldName: string; levelRange: string; themeColor: string }>;
  for (const world of WORLDS) {
    const enemyType = WORLD_ENEMY_MAP[world.id];
    if (enemyType) {
      ctx[enemyType] = {
        worldName: world.name.replace(/^World \d+:\s*/, ''),
        levelRange: `Levels ${world.levelStart + 1}-${world.levelEnd + 1}`,
        themeColor: world.visual.glow,
      };
    }
  }
  return ctx;
})();

const CELL_SIZE = 80;

function makeMockEnemy(type: WorldEnemyType, index: number): EnemyState {
  return {
    id: `test-${type}`,
    type,
    position: [index, 0] as Position,
    previousPosition: [index, 0] as Position,
    direction: 'right',
    isStunned: false,
    turnsUntilActive: 0,
  };
}

interface EntityCompareScreenProps {
  onGoHome: () => void;
}

const EntityCompareScreen: React.FC<EntityCompareScreenProps> = ({ onGoHome }) => {
  const navigate = useNavigate();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#1a1a2e',
        padding: 20,
        paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <motion.div
        style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <GameButton onClick={() => navigate('/test')} variant="secondary" icon="home" text="Back" />
        <div>
          <h1 style={{ color: COLOR.textWhite, fontSize: FONT_SIZE.modalTitle, fontFamily: FONT.heading, margin: 0 }}>
            Entity Compare
          </h1>
          <p style={{ color: COLOR.textSecondary, fontSize: 11, margin: '4px 0 0' }}>
            Character vs All Enemy Types - Visual Comparison
          </p>
        </div>
      </motion.div>

      {/* Row 1: Normal state at 80px */}
      <Section title="Normal State (80px cell)">
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <EntitySlot label="Character" color="#4FC3F7">
            <div style={{ position: 'relative', width: CELL_SIZE, height: CELL_SIZE }}>
              <Character3D
                position={[0, 0]}
                direction={null}
                isMoving={false}
                cellSize={CELL_SIZE}
                gameState="playing"
              />
            </div>
          </EntitySlot>
          {ENEMY_TYPES.map((type) => {
            const ctx = ENEMY_WORLD_CONTEXT[type];
            const def = ENEMY_DEFINITIONS[type];
            return (
              <EntitySlot
                key={type}
                label={def.name}
                color={def.accentColor}
                subtitle={`${ctx.worldName} (${ctx.levelRange})`}
                dotColor={ctx.themeColor}
              >
                <div style={{ position: 'relative', width: CELL_SIZE, height: CELL_SIZE }}>
                  <Enemy3D
                    enemy={makeMockEnemy(type, 0)}
                    cellSize={CELL_SIZE}
                  />
                </div>
              </EntitySlot>
            );
          })}
        </div>
      </Section>

      {/* Row 2: Large size for detail inspection */}
      <Section title="Large (120px cell) - Detail View">
        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <EntitySlot label="Character" color="#4FC3F7">
            <div style={{ position: 'relative', width: 120, height: 120 }}>
              <Character3D
                position={[0, 0]}
                direction={null}
                isMoving={false}
                cellSize={120}
                gameState="playing"
              />
            </div>
          </EntitySlot>
          {ENEMY_TYPES.map((type) => {
            const ctx = ENEMY_WORLD_CONTEXT[type];
            const def = ENEMY_DEFINITIONS[type];
            return (
              <EntitySlot
                key={type}
                label={def.name}
                color={def.accentColor}
                subtitle={`${ctx.worldName} (${ctx.levelRange})`}
                dotColor={ctx.themeColor}
              >
                <div style={{ position: 'relative', width: 120, height: 120 }}>
                  <Enemy3D
                    enemy={makeMockEnemy(type, 0)}
                    cellSize={120}
                  />
                </div>
              </EntitySlot>
            );
          })}
        </div>
      </Section>

      {/* Row 3: Frozen state */}
      <Section title="Frozen State">
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {ENEMY_TYPES.map((type) => {
            const ctx = ENEMY_WORLD_CONTEXT[type];
            const def = ENEMY_DEFINITIONS[type];
            return (
              <EntitySlot
                key={type}
                label={def.name}
                color="#8ecfff"
                subtitle={ctx.worldName}
                dotColor={ctx.themeColor}
              >
                <div style={{ position: 'relative', width: CELL_SIZE, height: CELL_SIZE }}>
                  <Enemy3D
                    enemy={makeMockEnemy(type, 0)}
                    cellSize={CELL_SIZE}
                    isFrozen={true}
                  />
                </div>
              </EntitySlot>
            );
          })}
        </div>
      </Section>

      {/* Row 4: Stunned state */}
      <Section title="Stunned State">
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {ENEMY_TYPES.map((type) => {
            const ctx = ENEMY_WORLD_CONTEXT[type];
            const def = ENEMY_DEFINITIONS[type];
            const enemy = makeMockEnemy(type, 0);
            enemy.isStunned = true;
            return (
              <EntitySlot
                key={type}
                label={def.name}
                color="#FFD54F"
                subtitle={ctx.worldName}
                dotColor={ctx.themeColor}
              >
                <div style={{ position: 'relative', width: CELL_SIZE, height: CELL_SIZE }}>
                  <Enemy3D
                    enemy={enemy}
                    cellSize={CELL_SIZE}
                  />
                </div>
              </EntitySlot>
            );
          })}
        </div>
      </Section>

      {/* Row 5: Alert state (player nearby) */}
      <Section title="Alert State (player within 3 tiles)">
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {ENEMY_TYPES.map((type) => {
            const ctx = ENEMY_WORLD_CONTEXT[type];
            const def = ENEMY_DEFINITIONS[type];
            return (
              <EntitySlot
                key={type}
                label={def.name}
                color="#EF5350"
                subtitle={ctx.worldName}
                dotColor={ctx.themeColor}
              >
                <div style={{ position: 'relative', width: CELL_SIZE, height: CELL_SIZE }}>
                  <Enemy3D
                    enemy={makeMockEnemy(type, 0)}
                    cellSize={CELL_SIZE}
                    playerPosition={[2, 0]}
                  />
                </div>
              </EntitySlot>
            );
          })}
        </div>
      </Section>

      {/* Row 6: Small size (game-realistic) */}
      <Section title="Game Size (48px cell)">
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <EntitySlot label="Character" color="#4FC3F7">
            <div style={{ position: 'relative', width: 48, height: 48 }}>
              <Character3D
                position={[0, 0]}
                direction={null}
                isMoving={false}
                cellSize={48}
                gameState="playing"
              />
            </div>
          </EntitySlot>
          {ENEMY_TYPES.map((type) => {
            const def = ENEMY_DEFINITIONS[type];
            const ctx = ENEMY_WORLD_CONTEXT[type];
            return (
              <EntitySlot
                key={type}
                label={def.name}
                color={def.accentColor}
                dotColor={ctx.themeColor}
              >
                <div style={{ position: 'relative', width: 48, height: 48 }}>
                  <Enemy3D
                    enemy={makeMockEnemy(type, 0)}
                    cellSize={48}
                  />
                </div>
              </EntitySlot>
            );
          })}
        </div>
      </Section>

      {/* Row 7: Character states */}
      <Section title="Character States">
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <EntitySlot label="Idle" color="#4FC3F7">
            <div style={{ position: 'relative', width: CELL_SIZE, height: CELL_SIZE }}>
              <Character3D position={[0, 0]} direction={null} isMoving={false} cellSize={CELL_SIZE} gameState="playing" />
            </div>
          </EntitySlot>
          <EntitySlot label="Collided" color="#EF5350">
            <div style={{ position: 'relative', width: CELL_SIZE, height: CELL_SIZE }}>
              <Character3D position={[0, 0]} direction={null} isMoving={false} cellSize={CELL_SIZE} gameState="playing" hasCollided={true} />
            </div>
          </EntitySlot>
          <EntitySlot label="Lost" color={COLOR.disabled}>
            <div style={{ position: 'relative', width: CELL_SIZE, height: CELL_SIZE }}>
              <Character3D position={[0, 0]} direction={null} isMoving={false} cellSize={CELL_SIZE} gameState="lost" />
            </div>
          </EntitySlot>
        </div>
      </Section>
    </div>
  );
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <motion.div
      style={{ marginBottom: 40 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h2 style={{
        color: COLOR.textSecondary,
        fontSize: FONT_SIZE.hudPill,
        fontFamily: FONT.heading,
        marginBottom: 16,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        paddingBottom: 8,
      }}>
        {title}
      </h2>
      {children}
    </motion.div>
  );
}

function EntitySlot({ label, color, subtitle, dotColor, children }: {
  label: string;
  color: string;
  subtitle?: string;
  dotColor?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{
        background: COLOR.surfaceCard,
        borderRadius: RADIUS.md,
        border: `1px solid ${COLOR.glassBorder}`,
        padding: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {children}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <span style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          color,
          fontSize: 9,
          fontFamily: FONT.heading,
          textAlign: 'center',
          textTransform: 'capitalize',
        }}>
          {dotColor && (
            <span style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: RADIUS.full,
              background: dotColor,
              flexShrink: 0,
            }} />
          )}
          {label}
        </span>
        {subtitle && (
          <span style={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: 7,
            fontFamily: FONT.heading,
            textAlign: 'center',
          }}>
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

export default EntityCompareScreen;
